import getPieveAuthToken from '@/lib/auth/getPieveAuthToken.js';
import { normalizePieveThreadEventsForPresentation } from '@/lib/pieveThreadEventNormalization.js';
import parseThreadEventsPageResponse from '@/lib/threadEvents/parseThreadEventsPageResponse.js';
import { THREAD_TYPE_IN_BY_SCOPE } from '@/lib/threads/buildThreadListHydrationRequest.js';
import getThreadsById from '@/lib/threads/getThreadsById.js';
import normalizeThreadListScope from '@/lib/threads/normalizeThreadListScope.js';
import normalizeThreadRecord from '@/lib/threads/normalizeThreadRecord.js';
import normalizeThreadType from '@/lib/threads/normalizeThreadType.js';
import reconcileHydratedThreads from '@/lib/threads/reconcileHydratedThreads.js';
import { MAIN_THREAD_TYPE_NOT_IN } from '@/lib/threads/threadTypeValues.js';
import { attachHydratedDirectRemoteRunStreams } from '@/redux/actions/pieveActions/lib/directRemoteThreadRunTransport.js';
import setThreads from '@/redux/actions/pieveActions/setThreads.js';
import { invalidateApiRequestGetDedupeState } from '@/redux/requests/apiRequest.js';
import listThreadEventsPage from '@/redux/requests/threads/listThreadEventsPage.js';
import { getThreads as getThreadsRequest } from '@/redux/requests/threadsRequests.js';
import selectors from '@/redux/selectors/selectors.js';

const DEFAULT_EVENT_PAGE_LIMIT = 120;
const PROJECT_SECTION_THREAD_LIST_ENABLED = true;
const PROJECT_SECTION_THREAD_LIST_LIMIT = 30;
const THREAD_LIST_SCOPE_MAIN = 'main';
const THREAD_LIST_SCOPE_SCHEDULES = 'schedules';
const THREAD_LIST_SCOPE_TRIGGERS = 'triggers';
const THREAD_LIST_SCOPE_WORKFLOWS = 'workflows';
const DEBUG_THREADS = Boolean(import.meta.env?.DEV) && import.meta.env?.MODE !== 'test';
const latestHydrationRequestIdByScope = {
  [THREAD_LIST_SCOPE_MAIN]: 0,
  [THREAD_LIST_SCOPE_SCHEDULES]: 0,
  [THREAD_LIST_SCOPE_TRIGGERS]: 0,
  [THREAD_LIST_SCOPE_WORKFLOWS]: 0
};

const logThreadHydration = () => {};

const normalizeThreadTypeFilterValues = (value) => {
  if (value === undefined || value === null || value === '') {
    return [];
  }

  const rawItems = Array.isArray(value) ? value : [value];
  const normalizedValues = [];
  const seen = new Set();

  for (const rawItem of rawItems) {
    const splitValues = String(rawItem || '').split(',');

    for (const splitValue of splitValues) {
      const normalized = normalizeThreadType(splitValue, '');

      if (!normalized || seen.has(normalized)) {
        continue;
      }

      seen.add(normalized);
      normalizedValues.push(normalized);
    }
  }

  return normalizedValues;
};

const isSchedulesPath = (pathname) => {
  const rawPath = String(pathname || '').trim();
  const normalizedPath = !rawPath || rawPath === '/' ? '/' : rawPath.replace(/\/+$/, '');
  return normalizedPath === '/automations/schedules' || normalizedPath.startsWith('/automations/schedules/');
};

const resolveThreadTypeFiltersForHydration = ({ threadTypeIn, threadTypeNotIn } = {}) => {
  const normalizedThreadTypeIn = normalizeThreadTypeFilterValues(threadTypeIn);
  const normalizedThreadTypeNotIn = normalizeThreadTypeFilterValues(threadTypeNotIn);

  if (normalizedThreadTypeIn.length > 0 || normalizedThreadTypeNotIn.length > 0) {
    return {
      threadTypeIn: normalizedThreadTypeIn,
      threadTypeNotIn: normalizedThreadTypeNotIn
    };
  }

  const currentPathname = typeof window === 'undefined' ? '' : window?.location?.pathname;

  if (isSchedulesPath(currentPathname)) {
    return {
      threadTypeIn: [...THREAD_TYPE_IN_BY_SCOPE[THREAD_LIST_SCOPE_SCHEDULES]],
      threadTypeNotIn: []
    };
  }

  return {
    threadTypeIn: [],
    threadTypeNotIn: [...MAIN_THREAD_TYPE_NOT_IN]
  };
};

const isExactThreadTypeFilterMatch = ({ values = [], candidates = [] } = {}) => {
  const normalizedValues = normalizeThreadTypeFilterValues(values);
  const normalizedCandidates = normalizeThreadTypeFilterValues(candidates);

  if (normalizedValues.length !== normalizedCandidates.length) {
    return false;
  }

  const valueSet = new Set(normalizedValues);

  for (const candidate of normalizedCandidates) {
    if (!valueSet.has(candidate)) {
      return false;
    }
  }

  return true;
};

const resolveThreadListScopeForHydration = ({ threadTypeIn, threadTypeNotIn, threadListScope } = {}) => {
  const explicitScope = normalizeThreadListScope(threadListScope, '');

  if (explicitScope) {
    return explicitScope;
  }

  const normalizedThreadTypeNotIn = normalizeThreadTypeFilterValues(threadTypeNotIn);

  if (normalizedThreadTypeNotIn.length > 0) {
    return THREAD_LIST_SCOPE_MAIN;
  }

  if (isExactThreadTypeFilterMatch({ values: threadTypeIn, candidates: THREAD_TYPE_IN_BY_SCOPE[THREAD_LIST_SCOPE_SCHEDULES] })) {
    return THREAD_LIST_SCOPE_SCHEDULES;
  }

  if (isExactThreadTypeFilterMatch({ values: threadTypeIn, candidates: THREAD_TYPE_IN_BY_SCOPE[THREAD_LIST_SCOPE_TRIGGERS] })) {
    return THREAD_LIST_SCOPE_TRIGGERS;
  }

  if (isExactThreadTypeFilterMatch({ values: threadTypeIn, candidates: THREAD_TYPE_IN_BY_SCOPE[THREAD_LIST_SCOPE_WORKFLOWS] })) {
    return THREAD_LIST_SCOPE_WORKFLOWS;
  }

  return THREAD_LIST_SCOPE_MAIN;
};

const normalizeThread = (value, existingThreadsById = {}) => {
  const thread = value?.thread || value;
  const threadId = thread?.id || thread?.threadId || null;

  if (!thread || !threadId) {
    return null;
  }

  return normalizeThreadRecord({
    value: thread,
    existingThread: existingThreadsById?.[String(threadId)] || null
  });
};

const buildEventHydrationThreadIds = ({ threads, preferredThreadIds, routeThreadId }) => {
  const knownIds = new Set((threads || []).map((thread) => thread?.id).filter(Boolean));

  if (Array.isArray(preferredThreadIds) && preferredThreadIds.length > 0) {
    const next = [];
    const seen = new Set();

    for (const threadId of preferredThreadIds) {
      if (!threadId || seen.has(threadId) || !knownIds.has(threadId)) {
        continue;
      }

      seen.add(threadId);
      next.push(threadId);
    }

    return next;
  }

  if (routeThreadId && knownIds.has(routeThreadId)) {
    return [routeThreadId];
  }

  const firstThreadId = threads?.[0]?.id || null;
  return firstThreadId ? [firstThreadId] : [];
};

const getRawThreadsFromResponse = (response) => {
  const listData = response?.data;

  if (Array.isArray(listData)) {
    return listData;
  }

  if (Array.isArray(listData?.items)) {
    return listData.items;
  }

  if (Array.isArray(listData?.threads)) {
    return listData.threads;
  }

  return [];
};

const hydrateThreads = ({ threadIdsForEvents = null, skipEvents = false, forceRemote = false, authToken: explicitAuthToken = null, threadTypeIn = undefined, threadTypeNotIn = undefined, threadListScope = undefined } = {}) => {
  return async (dispatch, getState) => {
    const hydrationCompanyId = String(selectors.activeCompanyId(getState?.() || {}) || '').trim();
    const threadTypeFilters = resolveThreadTypeFiltersForHydration({
      threadTypeIn,
      threadTypeNotIn
    });
    const resolvedThreadListScope = resolveThreadListScopeForHydration({
      threadTypeIn: threadTypeFilters.threadTypeIn,
      threadTypeNotIn: threadTypeFilters.threadTypeNotIn,
      threadListScope
    });
    const shouldUseProjectSections = resolvedThreadListScope === THREAD_LIST_SCOPE_MAIN;
    const latestScopeHydrationRequestId = Number(latestHydrationRequestIdByScope?.[resolvedThreadListScope]) || 0;
    const hydrationRequestId = latestScopeHydrationRequestId + 1;
    latestHydrationRequestIdByScope[resolvedThreadListScope] = hydrationRequestId;
    const authToken = String(explicitAuthToken || '').trim() || (await getPieveAuthToken());
    const isLatestHydration = () => {
      if (hydrationRequestId !== (Number(latestHydrationRequestIdByScope?.[resolvedThreadListScope]) || 0)) {
        return false;
      }

      if (!hydrationCompanyId) {
        return true;
      }

      const currentCompanyId = String(selectors.activeCompanyId(getState?.() || {}) || '').trim();
      return currentCompanyId === hydrationCompanyId;
    };

    if (!isLatestHydration()) {
      logThreadHydration({
        stage: 'skip-stale-before-list',
        hydrationRequestId,
        threadListScope: resolvedThreadListScope,
        latestHydrationRequestId: Number(latestHydrationRequestIdByScope?.[resolvedThreadListScope]) || 0
      });
      return;
    }

    if (forceRemote) {
      invalidateApiRequestGetDedupeState();

      if (!isLatestHydration()) {
        return;
      }
    }

    const threadListRequest = {
      authToken,
      threadTypeIn: threadTypeFilters.threadTypeIn,
      threadTypeNotIn: threadTypeFilters.threadTypeNotIn,
      projectSections: shouldUseProjectSections ? PROJECT_SECTION_THREAD_LIST_ENABLED : false,
      perProjectLimit: shouldUseProjectSections ? PROJECT_SECTION_THREAD_LIST_LIMIT : null
    };
    const threadsRes = await getThreadsRequest(threadListRequest);

    if (!threadsRes?.success) {
      logThreadHydration({
        stage: 'list-failed',
        hasAuthToken: Boolean(authToken),
        error: threadsRes?.data || null
      });
      return threadsRes || { success: false, message: 'Failed to load tasks' };
    }

    if (!isLatestHydration()) {
      logThreadHydration({
        stage: 'skip-stale-after-list',
        hydrationRequestId,
        threadListScope: resolvedThreadListScope,
        latestHydrationRequestId: Number(latestHydrationRequestIdByScope?.[resolvedThreadListScope]) || 0
      });
      return;
    }

    const listData = threadsRes?.data;
    const rawThreads = getRawThreadsFromResponse({
      data: listData
    });
    const existingThreadsById = getThreadsById(getState?.()?.pieve?.threads || []);
    let threads = rawThreads.map((thread) => normalizeThread(thread, existingThreadsById)).filter(Boolean);

    logThreadHydration({
      stage: 'list-success',
      hasAuthToken: Boolean(authToken),
      threadCount: threads.length,
      threadIds: threads.map((thread) => thread?.id).filter(Boolean)
    });

    const stateBeforeProjectMerge = getState?.() || {};
    const activeProjectId = selectors.activeProjectId(stateBeforeProjectMerge);
    const projectMetaById = selectors.projectMetaById(stateBeforeProjectMerge) || {};
    const existingThreadContent = selectors.threadContentItems(stateBeforeProjectMerge);
    threads = reconcileHydratedThreads({
      incomingThreads: threads,
      existingThreads: stateBeforeProjectMerge?.pieve?.threads || [],
      existingThreadContent,
      projectMetaById,
      activeProjectId
    });

    if (!isLatestHydration()) {
      logThreadHydration({
        stage: 'skip-stale-before-events',
        hydrationRequestId,
        threadListScope: resolvedThreadListScope,
        latestHydrationRequestId: Number(latestHydrationRequestIdByScope?.[resolvedThreadListScope]) || 0
      });
      return;
    }

    const state = getState?.() || {};
    const routeThreadId = selectors.routeThreadId(state);
    const eventsByThreadId = {};

    for (const thread of threads) {
      const existingEvents = selectors.threadContent(state, thread.id)?.activity;

      if (Array.isArray(existingEvents) && existingEvents.length > 0) {
        eventsByThreadId[thread.id] = normalizePieveThreadEventsForPresentation(existingEvents);
      }
    }

    const eventHydrationThreadIds = skipEvents
      ? []
      : buildEventHydrationThreadIds({
          threads,
          preferredThreadIds: threadIdsForEvents,
          routeThreadId
        });

    if (eventHydrationThreadIds.length > 0) {
      const eventResponses = await Promise.all(
        eventHydrationThreadIds.map(async (threadId) => {
          const eventsRes = await listThreadEventsPage({
            authToken,
            threadId,
            limit: DEFAULT_EVENT_PAGE_LIMIT
          });

          return {
            threadId,
            eventsRes
          };
        })
      );

      for (const response of eventResponses) {
        if (!response?.eventsRes?.success) {
          continue;
        }

        eventsByThreadId[response.threadId] = parseThreadEventsPageResponse(response.eventsRes).events;
      }
    }

    if (!isLatestHydration()) {
      logThreadHydration({
        stage: 'skip-stale-before-dispatch',
        hydrationRequestId,
        threadListScope: resolvedThreadListScope,
        latestHydrationRequestId: Number(latestHydrationRequestIdByScope?.[resolvedThreadListScope]) || 0
      });
      return;
    }

    const setThreadsPayload = {
      threads,
      eventsByThreadId,
      threadListScope: resolvedThreadListScope,
      companyId: hydrationCompanyId || null
    };

    if (shouldUseProjectSections) {
      setThreadsPayload.sectionCursors = listData?.projectSections?.sectionCursors || {};
    }

    dispatch(setThreads(setThreadsPayload));
    await attachHydratedDirectRemoteRunStreams({
      dispatch,
      getState,
      threads,
      authToken
    });

    logThreadHydration({
      stage: 'set-threads-dispatched',
      threadCount: threads.length,
      eventThreadCount: Object.keys(eventsByThreadId).length,
      eventHydrationThreadIds,
      projectCount: Object.keys(projectMetaById).length,
      activeProjectId,
      threadListScope: resolvedThreadListScope
    });

    if (DEBUG_THREADS && typeof window !== 'undefined') {
      window.__pieveReducerSnapshot = getState?.()?.pieve || null;
    }

    return {
      success: true,
      data: setThreadsPayload
    };
  };
};

export default hydrateThreads;
